/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Row, Space, Tooltip, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { SWITCH_QUERY_HANDLE_IDS } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';

// Constant declarations
const { Text } = Typography;

const NlpFlowQueryNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position="top" />

        <div className="content">
          <Space size={6} className="node-header">
            {nodeProps.icon}
            {nodeProps.label}
          </Space>

          {
            data.query ? (
              <Tooltip
                placement="leftBottom"
                title={data.query}
                arrowPointAtCenter
              >
                <Text className="text-color-default" ellipsis>
                  {data.query}
                </Text>
              </Tooltip>
            ) : (
              <Text className="text-secondary">
                {t('Click to edit content')}
              </Text>
            )
          }
        </div>

        <Row justify="space-evenly" align="stretch">
          <Col>
            <LimitedConnectionsHandle
              type="source"
              id={SWITCH_QUERY_HANDLE_IDS.TRUE}
              source={id}
              className="border-color-success"
              position="bottom"
              allowedOutgoingEdgesCount={1}
            />
          </Col>

          <Col>
            <LimitedConnectionsHandle
              type="source"
              id={SWITCH_QUERY_HANDLE_IDS.FALSE}
              source={id}
              className="border-color-error"
              position="bottom"
              allowedOutgoingEdgesCount={1}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [data, nodeProps]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
NlpFlowQueryNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(NlpFlowQueryNode);
